import {
  ChevronLeft20Regular,
  ChevronRight20Regular,
  ChevronDoubleLeft20Regular,
  ChevronDoubleRight20Regular,
} from "@fluentui/react-icons";
import { mergeClasses, Tooltip } from "@fluentui/react-components";
import { useAdminPaginationFooterStyles } from "./AdminPaginationFooter.styles";

interface AdminPaginationFooterProps {
  selectedIndex: number;
  pageCount: number;
  itemsPerPage: number;
  totalItemsCount: number;
  onPageChange: (index: number) => void;
}

export const AdminPaginationFooter = (
  props: AdminPaginationFooterProps,
): JSX.Element => {
  const {
    selectedIndex,
    pageCount,
    itemsPerPage,
    totalItemsCount,
    onPageChange,
  } = props;

  const styles = useAdminPaginationFooterStyles();

  const startItem =
    totalItemsCount === 0 ? 0 : selectedIndex * itemsPerPage + 1;
  const endItem = Math.min((selectedIndex + 1) * itemsPerPage, totalItemsCount);

  const canGoPrevious = selectedIndex > 0;
  const canGoNext = selectedIndex < pageCount - 1;

  const getVisiblePages = (): number[] => {
    const maxVisible = 5;
    if (pageCount <= maxVisible) {
      return Array.from({ length: pageCount }, (_, i) => i);
    }

    const half = Math.floor((maxVisible - 1) / 2);
    let start = selectedIndex - half;
    let end = selectedIndex + (maxVisible - 1 - half);

    if (start < 0) {
      start = 0;
      end = maxVisible - 1;
    } else if (end >= pageCount) {
      end = pageCount - 1;
      start = end - maxVisible + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div className={styles.container}>
      <nav aria-label="Pagination" style={{ width: "100%" }}>
        <div className={styles.paginatorWrapper}>
          <div className={styles.infoText} aria-live="polite">
            Showing {startItem}-{endItem} of {totalItemsCount} items
          </div>

          <div className={styles.paginatorContainer}>
            <Tooltip content="First page" relationship="label">
              <button
                className={styles.navButton}
                onClick={() => onPageChange(0)}
                disabled={!canGoPrevious}
                aria-label="First page"
              >
                <ChevronDoubleLeft20Regular />
              </button>
            </Tooltip>

            <Tooltip content="Previous page" relationship="label">
              <button
                className={styles.navButton}
                onClick={() => onPageChange(selectedIndex - 1)}
                disabled={!canGoPrevious}
                aria-label="Previous page"
              >
                <ChevronLeft20Regular />
              </button>
            </Tooltip>

            {getVisiblePages().map((pageIndex) => (
              <button
                key={pageIndex}
                className={mergeClasses(
                  styles.pageButton,
                  pageIndex === selectedIndex && styles.pageButtonActive,
                )}
                onClick={() => onPageChange(pageIndex)}
                aria-label={`Page ${pageIndex + 1} of ${pageCount}`}
                aria-current={pageIndex === selectedIndex ? "page" : undefined}
              >
                {pageIndex + 1}
              </button>
            ))}

            <Tooltip content="Next page" relationship="label">
              <button
                className={styles.navButton}
                onClick={() => onPageChange(selectedIndex + 1)}
                disabled={!canGoNext}
                aria-label="Next page"
              >
                <ChevronRight20Regular />
              </button>
            </Tooltip>

            <Tooltip content="Last page" relationship="label">
              <button
                className={styles.navButton}
                onClick={() => onPageChange(pageCount - 1)}
                disabled={!canGoNext}
                aria-label="Last page"
              >
                <ChevronDoubleRight20Regular />
              </button>
            </Tooltip>
          </div>
        </div>
      </nav>
    </div>
  );
};
